import { TEXT_ALIGNMENT } from 'constants/fonts';
import { FONT_FAMILY_CONFIG } from 'constants/fonts';
import { ColorPicker } from 'modules/Editor/components/ColorPicker';
import { ShapeSwap } from 'modules/Editor/components/ShapeSwap';
import { BorderStyle, BorderStyleSelect, BorderWidthPopover } from 'modules/common/components/Border';
import { FontFamilySelect } from 'modules/common/components/FontFamilySelect';
import { FontSizeSelect } from 'modules/common/components/FontSizeSelect';
import { Bold, Italic, Underline } from 'modules/common/components/FontStyle';
import { FontFamily } from 'types/font.types';
import { TextAlignment } from 'widgets/TextBasedWidgets/common/toolbar/TextAlignment';

import { MultiEditChangeOption, MultiEditOption } from '../../../Widget.types';
import { OptionProps } from './MultiWidgetToolbarOptions.types';
import { useMultiWidgetValue } from './MultiWidgetValueProvider';

export const MultiBorder = ({ onBatchUpdate }: OptionProps) => {
  const { borderColor } = useMultiWidgetValue();
  const value = borderColor ? borderColor[0] : '';

  // only use unique colors for the color picker icon
  const iconColors = [...new Set(borderColor)] as string[];
  const onChange = (color: string) => onBatchUpdate(MultiEditOption.BorderColor, color, MultiEditChangeOption.OnChange);

  return (
    <ColorPicker
      iconColors={iconColors}
      color={value}
      onChange={onChange}
      label={'Border Color'}
      iconStyle={'border'}
      showNoColorChecker={true}
    />
  );
};

export const MultiColor = ({ onBatchUpdate }: OptionProps) => {
  const { color } = useMultiWidgetValue();
  const value = color[0];

  // only use unique colors for the color picker icon
  const iconColors = [...new Set(color)] as string[];
  const onChange = (color: string) => onBatchUpdate(MultiEditOption.Color, color, MultiEditChangeOption.OnChange);

  return (
    <ColorPicker
      color={value}
      iconColors={iconColors}
      label='Color'
      onChange={onChange}
      showNoColorOption={false}
      showNoColorChecker={true}
    />
  );
};

export const MultiTextColor = ({ onBatchUpdate }: OptionProps) => {
  const { textColor } = useMultiWidgetValue();
  const value = textColor[0];

  // only use unique colors for the color picker icon
  const iconColors = [...new Set(textColor)] as string[];
  const onChange = (color: string) => onBatchUpdate(MultiEditOption.TextColor, color, MultiEditChangeOption.OnChange);

  return (
    <ColorPicker
      iconStyle='text'
      color={value}
      iconColors={iconColors}
      label='Color'
      onChange={onChange}
      showNoColorOption={false}
      showNoColorChecker={true}
    />
  );
};

export const MultiTextAlign = ({ onBatchUpdate }: OptionProps) => {
  const { textAlign } = useMultiWidgetValue();
  const value = textAlign[0];

  const onChange = (textAlign: TEXT_ALIGNMENT) =>
    onBatchUpdate(MultiEditOption.TextAlign, textAlign, MultiEditChangeOption.OnChange);

  return <TextAlignment value={value} onChange={onChange} />;
};

export const MultiUnderline = ({ onBatchUpdate }: OptionProps) => {
  const { textDecoration } = useMultiWidgetValue();
  const value = textDecoration[0];
  const isActive = allValuesSame([...textDecoration]) ? value : false;

  const onChange = () => onBatchUpdate(MultiEditOption.TextDecoration, !isActive, MultiEditChangeOption.OnChange);

  return <Underline isActive={isActive} onChange={onChange} />;
};

export const MultiItalic = ({ onBatchUpdate }: OptionProps) => {
  const { fontStyle, fontFamily } = useMultiWidgetValue();
  const value = fontStyle[0];
  const fontFamilyValue = fontFamily[0];

  // if all fonts have italic disabled
  const isAllItalicDisabled = fontFamily.every((font) => !FONT_FAMILY_CONFIG[font as FontFamily]?.hasItalic);
  const isActive = allValuesSame([...fontStyle]) ? value : false;

  const onChange = () => onBatchUpdate(MultiEditOption.FontStyle, !isActive, MultiEditChangeOption.OnChange);

  return (
    <Italic isDisabled={isAllItalicDisabled} fontFamily={fontFamilyValue} isActive={isActive} onChange={onChange} />
  );
};

export const MultiBold = ({ onBatchUpdate }: OptionProps) => {
  const { fontWeight, fontFamily } = useMultiWidgetValue();
  const value = fontWeight[0];
  const fontFamilyValue = fontFamily[0];

  // if all fonts have bold disabled
  const isAllBoldDisabled = fontFamily.every((font) => !FONT_FAMILY_CONFIG[font as FontFamily]?.hasBold);
  const isActive = allValuesSame([...fontWeight]) ? value : false;

  const onChange = () => onBatchUpdate(MultiEditOption.FontWeight, !isActive, MultiEditChangeOption.OnChange);

  return <Bold isDisabled={isAllBoldDisabled} fontFamily={fontFamilyValue} isActive={isActive} onChange={onChange} />;
};

export const MultiBorderStyle = ({ onBatchUpdate }: OptionProps) => {
  const { borderStyle } = useMultiWidgetValue();
  const value = borderStyle[0];

  const onChange = (borderStyle: BorderStyle) =>
    onBatchUpdate(MultiEditOption.BorderStyle, borderStyle, MultiEditChangeOption.OnChange);

  return <BorderStyleSelect value={value} onChange={onChange} />;
};

export const MultiBorderWidth = ({ onBatchUpdate }: OptionProps) => {
  const { borderWidth } = useMultiWidgetValue();
  const value = borderWidth[0];

  const onChange = (borderWidth: number) =>
    onBatchUpdate(MultiEditOption.BorderWidth, borderWidth, MultiEditChangeOption.OnChange);

  return <BorderWidthPopover value={value} onChange={onChange} />;
};

export const MultiWidgetFontSize = ({ onBatchUpdate }: OptionProps) => {
  const { fontSize } = useMultiWidgetValue();
  const value = fontSize[0];
  const isSameFontSize = allValuesSame([...fontSize]);
  const onChange = (newFontSize: number) => {
    onBatchUpdate(MultiEditOption.FontSize, newFontSize, MultiEditChangeOption.OnChange);
  };

  const onIncrement = () => onBatchUpdate(MultiEditOption.FontSize, undefined, MultiEditChangeOption.OnIncrement);
  const onDecrement = () => onBatchUpdate(MultiEditOption.FontSize, undefined, MultiEditChangeOption.OnDecrement);

  return (
    <FontSizeSelect
      onChange={onChange}
      value={value}
      onIncrement={onIncrement}
      onDecrement={onDecrement}
      triggerBlurOnUnmount={false}
      isMultipleFontSize={!isSameFontSize}
    />
  );
};

const allValuesSame = (arr: any[]) => arr.every((val) => val === arr[0]);

export const MultiWidgetFontFamily = ({ onBatchUpdate }: OptionProps) => {
  const { fontFamily } = useMultiWidgetValue();
  const isSameFontFamily = allValuesSame([...fontFamily]);
  const fontFamilyValue = fontFamily[0];

  const onChange = (fontFamily: string) =>
    onBatchUpdate(MultiEditOption.FontFamily, fontFamily, MultiEditChangeOption.OnChange);

  return (
    <FontFamilySelect
      hasReturnFocus={false}
      isMultipleFont={!isSameFontFamily}
      fontFamily={fontFamilyValue}
      onChange={onChange}
      borderRadius='base'
      type='toolbar'
      buttonProps={{
        width: 36,
        height: 8,
      }}
    />
  );
};

export const MultiWidgetBasicShape = ({ onBatchUpdate }: OptionProps) => {
  return <ShapeSwap onChange={onBatchUpdate} />;
};
